import { useEffect, useState } from 'react'
import {
  Play, Pause, Plus, AlertTriangle, CheckCircle2, Clock, LogOut, RefreshCw,
  User, Factory, Layers, Square, Loader2, Siren, FlaskConical,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { kioskApi, type KioskState } from './kiosk-api'
import KioskApontamento from './KioskApontamento'
import KioskPausa from './KioskPausa'
import KioskRefugo from './KioskRefugo'
import KioskSOS from './KioskSOS'
import KioskLaudo from './KioskLaudo'

type OPItem = {
  op_id: number; op_numero: number;
  produto_codigo: string | null; produto_descricao: string;
  variante: string;
  quantidade: string; quantidade_produzida: string;
  peso_planejado: string; peso_produzido: string;
  etapa_op_id: number; etapa_codigo: string; etapa_nome: string;
  quantidade_ok: string; quantidade_refugo: string;
  peso_ok: string; peso_refugo: string;
  iniciada_em: string | null; pausada_em: string | null;
  status: 'pendente' | 'em_execucao' | 'concluida' | 'pulada';
  op_status: string;
  fim_previsto: string | null; pedido_id: number | null;
  remessa_codigo: string | null;
}

type Props = {
  state: KioskState
  onLogout: () => void
  onUnpair: () => void
  onStateChange: (s: KioskState) => void
}

export default function KioskHome({ state, onLogout, onUnpair }: Props) {
  const [fila, setFila] = useState<OPItem[]>([])
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState<null | 'apontar' | 'concluir' | 'pausar' | 'refugo' | 'sos' | 'laudo'>(null)

  const selected = fila.find(o => o.etapa_op_id === selectedId) || null
  const emExecucao = fila.find(o => o.status === 'em_execucao') || null

  async function refresh() {
    try {
      const r = await kioskApi.get<OPItem[]>('/kiosk/fila')
      setFila(r.data)
      // se nada selecionado, selecionar a em execução, ou a primeira
      if (selectedId == null) {
        const auto = r.data.find(o => o.status === 'em_execucao') || r.data[0]
        if (auto) setSelectedId(auto.etapa_op_id)
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar fila')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { refresh() }, [])
  useEffect(() => { const iv = setInterval(refresh, 30_000); return () => clearInterval(iv) }, [])

  async function iniciarEtapa(etapa_op_id: number) {
    try {
      await kioskApi.post('/kiosk/etapa/iniciar', { etapa_op_id })
      toast.success('Etapa iniciada')
      refresh()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  async function retornar(etapa_op_id: number) {
    try {
      await kioskApi.post('/kiosk/retorno', { etapa_op_id })
      toast.success('Retomado')
      refresh()
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Erro')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-violet-600" />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="px-4 py-3 bg-white border-b border-ink-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center">
            <Factory className="h-5 w-5" />
          </div>
          <div>
            <div className="text-xs text-ink-500 uppercase">Máquina</div>
            <div className="font-bold text-ink-900 text-sm">{state.machine?.codigo} — {state.machine?.nome}</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-xs text-ink-500 uppercase">{state.turno_nome ? `Turno ${state.turno_nome}` : 'Sem turno'}</div>
            <div className="font-medium text-ink-900 text-sm flex items-center gap-1 justify-end">
              <User className="h-3 w-3" /> {state.operator_nome}
            </div>
          </div>
          <button onClick={refresh} className="h-10 w-10 rounded-lg bg-ink-100 hover:bg-ink-200 flex items-center justify-center" title="Atualizar">
            <RefreshCw className="h-5 w-5" />
          </button>
          <button onClick={onLogout} className="h-10 px-3 rounded-lg bg-ink-100 hover:bg-red-100 hover:text-red-700 flex items-center gap-1 text-sm" title="Trocar operador">
            <LogOut className="h-4 w-4" /> Sair
          </button>
          <button
            onClick={() => setModal('laudo')}
            className="h-10 px-3 rounded-lg bg-violet-100 hover:bg-violet-200 text-violet-700 flex items-center gap-1 text-sm"
            title="Laudo de qualidade"
          >
            <FlaskConical className="h-4 w-4" /> Laudo
          </button>
          <button
            onClick={() => setModal('sos')}
            className="h-10 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white flex items-center gap-1 text-sm font-bold"
            title="Chamar ajuda"
          >
            <Siren className="h-4 w-4" /> SOS
          </button>
          <button onClick={onUnpair} className="h-10 px-2 rounded-lg bg-ink-100 hover:bg-red-100 hover:text-red-700 text-xs" title="Desparear máquina">
            ⋮
          </button>
        </div>
      </header>

      <div className="flex-1 flex flex-col lg:flex-row p-4 gap-4">
        {/* FILA */}
        <aside className="lg:w-1/3 space-y-2 lg:max-h-[calc(100vh-100px)] lg:overflow-auto">
          <div className="text-xs uppercase text-ink-500 font-semibold flex items-center gap-2">
            <Layers className="h-3 w-3" /> Fila ({fila.length})
          </div>
          {fila.length === 0 && (
            <div className="bg-white rounded-xl p-6 text-center text-ink-500 text-sm border border-ink-200">
              Nenhuma OP na fila pra essa máquina
            </div>
          )}
          {fila.map(op => (
            <button
              key={op.etapa_op_id}
              onClick={() => setSelectedId(op.etapa_op_id)}
              className={`w-full text-left p-3 rounded-xl border-2 transition bg-white ${
                selectedId === op.etapa_op_id
                  ? 'border-violet-500 shadow-md'
                  : 'border-ink-200 hover:border-ink-300'
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-1">
                <div className="text-xs font-mono text-ink-500">OP #{op.op_numero}</div>
                <StatusChip status={op.status} pausada={!!op.pausada_em} />
              </div>
              <div className="font-semibold text-sm text-ink-900 line-clamp-2">{op.produto_descricao}</div>
              <div className="flex items-center justify-between mt-1 text-xs">
                <span className="text-ink-500">{op.etapa_nome}</span>
                <span className="font-medium">{Number(op.quantidade_produzida).toLocaleString('pt-BR')}/{Number(op.quantidade).toLocaleString('pt-BR')}</span>
              </div>
              {op.remessa_codigo && (
                <div className="text-xs text-violet-700 mt-1">📦 {op.remessa_codigo}</div>
              )}
            </button>
          ))}
        </aside>

        {/* DETALHE + AÇÕES */}
        <main className="flex-1 space-y-4">
          {selected ? (
            <>
              <div className="bg-white rounded-2xl border border-ink-200 p-5 shadow-sm">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <div className="text-xs font-mono text-ink-500 mb-1">OP #{selected.op_numero}</div>
                    <h2 className="text-xl font-bold text-ink-900">{selected.produto_descricao}</h2>
                    <div className="flex items-center gap-2 mt-1 text-sm text-ink-500">
                      {selected.produto_codigo && <span className="font-mono">{selected.produto_codigo}</span>}
                      <span>•</span>
                      <span>{selected.variante}</span>
                      {selected.fim_previsto && (
                        <>
                          <span>•</span>
                          <Clock className="h-3 w-3" />
                          <span>até {new Date(selected.fim_previsto).toLocaleDateString('pt-BR')}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <StatusChip status={selected.status} pausada={!!selected.pausada_em} large />
                </div>

                <div className="bg-violet-50 rounded-xl p-3 mb-3">
                  <div className="text-xs uppercase text-violet-700 font-semibold">Etapa atual</div>
                  <div className="font-bold text-violet-900">{selected.etapa_nome}</div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Indicador label="Qtd OK" valor={`${Number(selected.quantidade_ok).toLocaleString('pt-BR')} / ${Number(selected.quantidade).toLocaleString('pt-BR')}`} />
                  <Indicador label="Refugo" valor={Number(selected.quantidade_refugo).toLocaleString('pt-BR')} variant="amber" />
                  <Indicador label="Peso OK" valor={`${Number(selected.peso_ok).toLocaleString('pt-BR')} kg`} />
                  <Indicador label="Peso refugo" valor={`${Number(selected.peso_refugo).toLocaleString('pt-BR')} kg`} variant="amber" />
                </div>
              </div>

              {/* AÇÕES */}
              <div className="grid grid-cols-2 gap-3">
                {selected.status === 'pendente' && (
                  <ActionButton
                    onClick={() => iniciarEtapa(selected.etapa_op_id)}
                    icon={Play} label="Iniciar etapa" color="emerald" full
                  />
                )}
                {selected.status === 'em_execucao' && !selected.pausada_em && (
                  <>
                    <ActionButton onClick={() => setModal('apontar')} icon={Plus} label="Apontar" color="violet" />
                    <ActionButton onClick={() => setModal('pausar')} icon={Pause} label="Pausar" color="amber" />
                    <ActionButton onClick={() => setModal('refugo')} icon={AlertTriangle} label="Refugo" color="red" />
                    <ActionButton onClick={() => setModal('concluir')} icon={Square} label="Concluir etapa" color="emerald" />
                  </>
                )}
                {selected.status === 'em_execucao' && selected.pausada_em && (
                  <ActionButton
                    onClick={() => retornar(selected.etapa_op_id)}
                    icon={Play} label="Retomar" color="emerald" full
                  />
                )}
                {selected.status === 'concluida' && (
                  <div className="col-span-2 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center gap-3 text-emerald-800">
                    <CheckCircle2 className="h-6 w-6" />
                    <div>
                      <div className="font-semibold">Etapa concluída</div>
                      <div className="text-xs">A próxima etapa pendente foi iniciada automaticamente.</div>
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="bg-white rounded-2xl border border-ink-200 p-12 text-center text-ink-500">
              Selecione uma OP da fila pra começar
            </div>
          )}
        </main>
      </div>

      {modal === 'apontar' && selected && (
        <KioskApontamento
          etapa_op_id={selected.etapa_op_id}
          modo="parcial"
          produtoNome={selected.produto_descricao}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); refresh() }}
        />
      )}
      {modal === 'concluir' && selected && (
        <KioskApontamento
          etapa_op_id={selected.etapa_op_id}
          modo="concluir"
          produtoNome={selected.produto_descricao}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); refresh() }}
        />
      )}
      {modal === 'pausar' && selected && (
        <KioskPausa
          etapa_op_id={selected.etapa_op_id}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); refresh() }}
        />
      )}
      {modal === 'refugo' && selected && (
        <KioskRefugo
          etapa_op_id={selected.etapa_op_id}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null); refresh() }}
        />
      )}
      {modal === 'sos' && (
        <KioskSOS
          etapa_op_id={selected?.etapa_op_id || null}
          onClose={() => setModal(null)}
          onDone={() => { setModal(null) }}
        />
      )}
      {modal === 'laudo' && (
        <KioskLaudo onClose={() => setModal(null)} />
      )}
    </div>
  )
}

function StatusChip({ status, pausada, large }: { status: string; pausada: boolean; large?: boolean }) {
  let label = status
  let cls = 'bg-ink-100 text-ink-700'
  if (pausada) { label = 'pausada'; cls = 'bg-amber-100 text-amber-800' }
  else if (status === 'em_execucao') { label = 'em execução'; cls = 'bg-emerald-100 text-emerald-800' }
  else if (status === 'pendente') { label = 'pendente'; cls = 'bg-blue-100 text-blue-800' }
  else if (status === 'concluida') { label = 'concluída'; cls = 'bg-violet-100 text-violet-800' }
  return (
    <span className={`px-2 py-0.5 rounded font-medium ${cls} ${large ? 'text-sm' : 'text-xs'}`}>
      {label}
    </span>
  )
}

function Indicador({ label, valor, variant }: { label: string; valor: string; variant?: 'amber' }) {
  return (
    <div className={`rounded-xl p-3 ${variant === 'amber' ? 'bg-amber-50' : 'bg-ink-50'}`}>
      <div className="text-xs uppercase text-ink-500 font-medium">{label}</div>
      <div className={`text-lg font-bold ${variant === 'amber' ? 'text-amber-800' : 'text-ink-900'}`}>{valor}</div>
    </div>
  )
}

function ActionButton({
  onClick, icon: Icon, label, color, full,
}: {
  onClick: () => void; icon: any; label: string;
  color: 'violet' | 'amber' | 'red' | 'emerald'; full?: boolean
}) {
  const colors = {
    violet: 'bg-violet-600 hover:bg-violet-700 active:bg-violet-800',
    amber: 'bg-amber-500 hover:bg-amber-600 active:bg-amber-700',
    red: 'bg-red-600 hover:bg-red-700 active:bg-red-800',
    emerald: 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800',
  }
  return (
    <button
      onClick={onClick}
      className={`h-20 rounded-xl text-white font-bold text-lg flex items-center justify-center gap-2 shadow-md transition ${colors[color]} ${full ? 'col-span-2' : ''}`}
    >
      <Icon className="h-6 w-6" /> {label}
    </button>
  )
}
